import React, { useState, useMemo, useEffect } from 'react';

function selectionKeyForTranslation(trans) {
  if (trans && trans._id) return String(trans._id);
  if (trans && trans.country && trans.language) return `${trans.country} - ${trans.language}`;
  return '';
}

const LocalizationModal = ({
  isOpen,
  onClose,
  onSave,
  translations = [],
  coa,
  wantOriginalTranslation: initialWantOriginalTranslation = false,
  initialTerritory = '',
  initialSelectedLocales = [],
  initialCustomRequest = ''
}) => {
  const [selectedLocales, setSelectedLocales] = useState([]);
  const [customRequest, setCustomRequest] = useState('');
  const [expandedCountries, setExpandedCountries] = useState([]);
  const [wantOriginalTranslation, setWantOriginalTranslation] = useState(initialWantOriginalTranslation);
  const [territory, setTerritory] = useState(initialTerritory);

  const regions = useMemo(() => {
    if (!translations || translations.length === 0) {
      return {};
    }

    const grouped = {};

    translations.forEach(trans => {
      if (!trans.country || !trans.language) return;

      const continent = trans.continent || 'Other';
      const country = trans.country;

      if (!grouped[continent]) {
        grouped[continent] = {};
      }

      if (!grouped[continent][country]) {
        grouped[continent][country] = [];
      }

      grouped[continent][country].push(trans);
    });

    Object.keys(grouped).forEach(continent => {
      Object.keys(grouped[continent]).forEach(country => {
        grouped[continent][country].sort((a, b) => {
          const c = (a.language || '').localeCompare(b.language || '');
          if (c !== 0) return c;
          return String(a._id || '').localeCompare(String(b._id || ''));
        });
      });
    });

    return grouped;
  }, [translations]);

  const getStatusBadge = (translation) => {
    const adminStatus = translation?.adminStatus;
    const status = translation?.status;
    const actualStatus = adminStatus;

    if (actualStatus === 'approved' || status === 'published' || status === 'active') {
      return (
        <span className="px-2 py-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 rounded-full">
          Available
        </span>
      );
    } else if (actualStatus === 'in_development' || status === 'in-progress' || actualStatus === 'pending') {
      return (
        <span className="px-2 py-0.5 text-[10px] font-medium text-yellow-600 bg-yellow-50 rounded-full">
          In Development
        </span>
      );
    } else if (actualStatus === 'rejected' || status === 'archived' || status === 'inactive') {
      return (
        <span className="px-2 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-50 rounded-full">
          {actualStatus === 'rejected' ? 'Rejected' : 'Archived'}
        </span>
      );
    } else {
      return (
        <span className="px-2 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-50 rounded-full">
          {String(actualStatus || '').replace(/[_-]+/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown'}
        </span>
      );
    }
  };

  useEffect(() => {
    if (translations.length > 0 && expandedCountries.length === 0) {
      const firstContinent = Object.keys(regions)[0];
      if (firstContinent && regions[firstContinent]) {
        const firstCountry = Object.keys(regions[firstContinent])[0];
        if (firstCountry) {
          setExpandedCountries([firstCountry]);
        }
      }
    }
  }, [translations, regions, expandedCountries.length]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedLocales(Array.isArray(initialSelectedLocales) ? initialSelectedLocales : []);
    setCustomRequest(initialCustomRequest ?? '');
    setWantOriginalTranslation(initialWantOriginalTranslation);
    setTerritory(initialTerritory ?? '');
  }, [
    isOpen,
    initialSelectedLocales,
    initialCustomRequest,
    initialWantOriginalTranslation,
    initialTerritory,
  ]);

  const toggleCountry = (country) => {
    setExpandedCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const toggleLocale = (key) => {
    if (!key) return;
    setSelectedLocales((prev) =>
      prev.includes(key) ? prev.filter((l) => l !== key) : [...prev, key]
    );
  };

  const isLocaleSelected = (key) => key && selectedLocales.includes(key);

  const sourceTranslation = useMemo(() => {
    return translations.find(trans => trans.isSourceVersion === true);
  }, [translations]);

  const sourceLanguage = coa?.details?.originalDevelopmentLanguage || sourceTranslation?.language || 'Original Language';
  const sourceTerritory = sourceTranslation?.territory || '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-[950px] max-h-[90vh] overflow-y-auto ">
        <div className="p-4">
          <div className="flex justify-end mb-2">
            <button
              onClick={onClose}
              className="text-[#73827C] hover:text-[#212529] transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M18 6L6 18M6 6L18 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <h2 className="text-[28px] font-semibold text-[#0A1F17] tracking-[-0.56px] leading-[1.4]">Select the currently available language version/translation.</h2>
        </div>

        <div className="px-4 pb-4 pt-2">
          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative w-6 h-6">
                <input
                  type="checkbox"
                  checked={wantOriginalTranslation}
                  onChange={(e) => setWantOriginalTranslation(e.target.checked)}
                  className="appearance-none w-6 h-6 border border-[#485B54] rounded checked:bg-[#40C191] checked:border-[#40C191] transition-colors cursor-pointer"
                />
                {wantOriginalTranslation && (
                  <svg
                    className="absolute inset-0 text-white pointer-events-none"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M20 6L9 17L4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="text-[14px] text-[#485B54] leading-[1.7]">
                I want to use the source language version – {sourceLanguage}{sourceTerritory ? ` / ${sourceTerritory}` : ''}
              </span>
            </label>
          </div>

          {translations.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#EBEBEB] p-6 text-center">
              <p className="text-[#73827C]">No translations available for this COA.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#EBEBEB] p-6">
              <div className="grid grid-cols-6 gap-x-6">
                {Object.entries(regions).map(([region, countries]) => (
                  <div key={region} className="space-y-4">
                    <h3 className="text-[18px] font-medium text-[#222F2A] leading-[1.4]">{region}</h3>
                    <div className="space-y-4">
                      {Object.entries(countries).map(([country, languages]) => (
                        <div key={country}>
                          <button
                            onClick={() => toggleCountry(country)}
                            className="flex items-center gap-3 px-3 w-full text-left group"
                          >
                            <div className="w-6 h-6 flex items-center justify-center text-[#73827C] group-hover:text-[#40C191] transition-colors">
                              {expandedCountries.includes(country) ? (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                                  <path d="M4 10H16" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                                  <path d="M10 4V16M4 10H16" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                              )}
                            </div>
                            <span className="text-[16px] font-medium text-[#73827C] group-hover:text-[#40C191] transition-colors leading-[1.6]">
                              {country}
                            </span>
                          </button>

                          {expandedCountries.includes(country) && (
                            <div className="mt-3 space-y-3 pl-4">
                              {languages.map((trans, idx) => {
                                const lang = trans.language;
                                const rowKey = selectionKeyForTranslation(trans);
                                const sub =
                                  trans.tag || trans.source || trans.note
                                    ? String(trans.tag || trans.source || trans.note).slice(0, 48)
                                    : '';
                                return (
                                  <div key={rowKey || `${country}-${lang}-${idx}`} className="flex flex-col gap-1">
                                    <div className="flex items-center gap-3">
                                      <label className="flex items-center gap-3 cursor-pointer">
                                        <div className="relative w-6 h-6">
                                          <input
                                            type="checkbox"
                                            checked={isLocaleSelected(rowKey)}
                                            onChange={() => toggleLocale(rowKey)}
                                            className="appearance-none w-6 h-6 border border-[#485B54] rounded checked:bg-[#40C191] checked:border-[#40C191] transition-colors"
                                          />
                                          {isLocaleSelected(rowKey) && (
                                            <svg
                                              className="absolute inset-0 text-white pointer-events-none"
                                              width="24"
                                              height="24"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                            >
                                              <path d="M20 6L9 17L4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                          )}
                                        </div>
                                        <span className="text-[14px] text-[#485B54] leading-[1.7]">
                                          {lang}
                                          {sub ? (
                                            <span className="block text-[11px] text-[#9CA3AF] font-normal mt-0.5">{sub}</span>
                                          ) : null}
                                        </span>
                                      </label>
                                    </div>
                                    <div className="pl-9">
                                      {getStatusBadge(trans)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="block text-sm text-[#212529] mb-2">
            Territory (or territories) you plan to use the COA in
            </label>
            <input
              type="text"
              value={territory}
              onChange={(e) => setTerritory(e.target.value)}
              placeholder="Enter territory or territories"
              className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
            />
          </div>

          <div className="mt-6 space-y-3">
            <p className="text-[14px] text-[#485B54] leading-[1.7]">
              If the translation you require for your intended use is not listed above, please use the free-text box below to enquire about its development.
            </p>
            <p className="text-[14px] text-[#485B54] leading-[1.7]">
              For example, please indicate if you wish to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[14px] text-[#485B54] leading-[1.7] ml-4">
              <li>Request a quotation for us to undertake the translation on your behalf;</li>
              <li>Seek permission to translate the COA yourself and if so, the methodology you plan to use. There is no guarantee permissions will be granted;</li>
              <li>If you select a translation currently in development, we will forward that translation as it becomes available. Please note, we cannot guarantee timelines with this option.</li>
            </ul>
            <p className="text-[14px] text-[#485B54] leading-[1.7]">
              Please be clear about your requirements to ensure efficient processing of your licence request.
            </p>
            <textarea
              value={customRequest}
              onChange={(e) => setCustomRequest(e.target.value)}
              className="w-full h-20 p-3 text-[14px] text-[#485B54] border border-[#EBEBEB] rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-[#40C191] leading-[1.7]"
            />

          </div>
        </div>

        <div className="p-4 flex justify-end">
          <button
            onClick={() => {
              onSave({ selectedLocales, customRequest, wantOriginalTranslation, territory });
              onClose();
            }}
            className="h-10 px-6 text-[14px] font-medium text-white bg-gradient-to-r from-[#40C191] to-[#5ED58D] rounded-lg hover:from-[#35A47C] hover:to-[#4EBF7A] transition-colors leading-[24px]"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocalizationModal;
